import clsx from 'clsx';
import React, { useCallback } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { CreateInventoryCategoryFormData, CreateInventoryCategorySchema } from '../../../services/inventory-category/schema/CreateInventoryCategorySchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateInventoryCategory } from '../../../services/inventory-category/hooks/useCreateInventoryCategory';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../auth/context/AuthProvider';

interface CreateInventoryCategoryDialogProps {
    show: boolean;
    onClose: () => void;
}

const CreateInventoryCategoryDialog: React.FC<CreateInventoryCategoryDialogProps> = ({ show, onClose }) => {
    const { user } = useAuth();

    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<CreateInventoryCategoryFormData>({
        resolver: zodResolver(CreateInventoryCategorySchema),
        defaultValues: {
            property_id: user?.active_property_id ?? '',
            name: '',
        }
    });

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    const { mutate, isPending } = useCreateInventoryCategory();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: CreateInventoryCategoryFormData) => {
        if (isPending) return;

        // Ensure property_id is set from auth
        const payload = {
            ...data,
            property_id: user?.active_property_id ?? data.property_id,
        };

        toast.loading('Creating inventory category...', { id: 'create-inventory-category' });
        mutate(payload, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['inventory-categories'].includes(key as string))
                });

                toast.success('Inventory category created successfully', { id: 'create-inventory-category' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof CreateInventoryCategoryFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create inventory category', { id: 'create-inventory-category' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to create inventory category', { id: 'create-inventory-category' });
                }
            }
        });
    }, [mutate, user, isPending, handleClose, queryClient, setError]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Inventory Category</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                    <div className="action-sheet-content flex-grow-1">
                        {/* Hidden property_id field */}
                        <input type="hidden" {...register('property_id')} />

                        {/* Name */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                    id="name"
                                    placeholder="Name"
                                    {...register('name')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.name && <div className="invalid-feedback">{errors.name.message}</div>}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                            {isPending ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default CreateInventoryCategoryDialog
